import React, { useState } from "react";
import { Box, Typography, Paper, Collapse, TextField } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

export const T = {
  pageBg: "var(--color-surface-2)",
  primary: "var(--color-primary)",
  ink: "var(--color-ink)",
  surface2: "var(--color-surface-2)",
  border: "var(--color-hairline-cloud)",
  performed: "var(--color-success)",
  missed: "var(--color-danger)",
  na: "rgba(31,22,51,0.6)",
};

// Single reader for an item's point value; falls back to legacy `pointsAwarded` on old sheets.
export const itemPoints = (item) => item?.pointValue ?? item?.pointsAwarded ?? 1;

// Total possible points of an item list (passingScore is in points, never an item count).
export const totalPoints = (items) => (items || []).reduce((sum, item) => sum + itemPoints(item), 0);

// ── Score result computed the same everywhere an item list is scored ─────────
export function computeChecklistResult(items, scores, passingScore) {
  const earned = items.reduce(
    (sum, item) => sum + (scores[item.id] === "performed" ? itemPoints(item) : 0), 0,
  );
  const total = items.reduce((sum, item) => sum + itemPoints(item), 0);
  const scoredCount = items.filter((item) => scores[item.id] != null).length;
  const criticalsFailed = items.filter((item) => item.isCritical && scores[item.id] === "missed");
  const hasPassingScore = passingScore != null && passingScore > 0;
  const status =
    scoredCount === 0
      ? "pending"
      : criticalsFailed.length > 0
        ? "fail"
        : hasPassingScore && earned >= passingScore
          ? "pass"
          : hasPassingScore && scoredCount === items.length
            ? "fail"
            : "pending";
  return { earned, total, scoredCount, criticalsFailed, status };
}

// ─── Score Donut ──────────────────────────────────────────────────────────────
export function ScoreDonut({ earned, total, status }) {
  const r = 30;
  const circ = 2 * Math.PI * r;
  const pct = total > 0 ? earned / total : 0;
  const dash = circ * pct;
  const color = status === "pass" ? T.performed : status === "fail" ? T.missed : T.primary;

  return (
    <Box sx={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
      <svg width={72} height={72} style={{ transform: "rotate(-90deg)", display: "block" }}>
        <circle cx={36} cy={36} r={r} fill="none" stroke={T.border} strokeWidth={5} />
        <circle cx={36} cy={36} r={r} fill="none" stroke={color} strokeWidth={5} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`} style={{ transition: "stroke-dasharray 0.4s ease" }} />
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color, lineHeight: 1, fontFamily: "var(--font-display)" }}>{Math.round(pct * 100)}%</Typography>
        <Typography sx={{ fontSize: 10, color: T.na, fontWeight: 600 }}>{earned}/{total}</Typography>
      </Box>
    </Box>
  );
}

// ─── Score Toggle ─────────────────────────────────────────────────────────────
// Segmented control: one solid fill per state, keyboard hint (P / M / N) shown on wide screens.
export function ScoreToggle({ value, onChange, disabled, isObservation, fullWidthOnMobile }) {
  const buttons = isObservation
    ? [
        { key: "performed", label: "Observed", hint: "P", fill: T.performed, Icon: CheckIcon },
        { key: "missed", label: "Not Observed", hint: "M", fill: T.missed, Icon: CloseIcon },
        { key: "na", label: "N/A", hint: "N", fill: T.ink, Icon: RemoveIcon },
      ]
    : [
        { key: "performed", label: "Performed", hint: "P", fill: T.performed, Icon: CheckIcon },
        { key: "missed", label: "Missed", hint: "M", fill: T.missed, Icon: CloseIcon },
        { key: "na", label: "N/A", hint: "N", fill: T.ink, Icon: RemoveIcon },
      ];

  return (
    <Box
      role="radiogroup"
      sx={{
        display: "grid", gridTemplateColumns: "repeat(3, auto)", alignSelf: "start", flexShrink: 0,
        bgcolor: "#fff", border: `1px solid ${T.border}`, borderRadius: "12px", overflow: "hidden",
        width: fullWidthOnMobile ? { xs: "100%", sm: "auto" } : "auto",
        "& > button:not(:first-of-type)": { borderLeft: `1px solid ${T.border}` },
      }}
    >
      {buttons.map(({ key, label, hint, fill, Icon }) => {
        const active = value === key;
        return (
          <Box
            key={key}
            component="button"
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(active ? null : key); }}
            sx={{
              height: 44, px: { xs: 1, sm: 1.75 }, border: 0, cursor: disabled ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
              fontFamily: "inherit", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
              bgcolor: active ? fill : "transparent", color: active ? "#fff" : T.ink, opacity: active ? 1 : 0.7,
              transition: "background-color .12s, opacity .12s",
              "&:hover": disabled ? {} : { opacity: 1, bgcolor: active ? fill : "var(--color-surface-press-light)" },
              "&:focus-visible": { outline: "2px solid var(--color-accent-violet)", outlineOffset: -2 },
            }}
          >
            <Icon sx={{ fontSize: 14 }} />
            {label}
            <Box component="span" sx={{ display: { xs: "none", md: "inline" }, fontFamily: "var(--font-mono)", fontSize: 10.5, opacity: active ? 0.8 : 0.5 }}>{hint}</Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Criteria Row ─────────────────────────────────────────────────────────────
export function CriteriaRow({ idx, item, score, note, onScore, onNote, locked, current, onFocusRow }) {
  const isObservation = (item.validationType || "").toLowerCase() === "observation";
  const noteRequired = score === "missed" && !note?.trim();
  const [showNote, setShowNote] = useState(score === "missed");

  React.useEffect(() => {
    if (score === "missed") setShowNote(true);
  }, [score]);

  const barColor =
    score === "performed" ? T.performed : score === "missed" ? T.missed : score === "na" ? "var(--color-hairline-cool)" : current ? "var(--color-accent-violet)" : "transparent";

  return (
    <Box
      data-testid="criteria-row"
      data-score={score || ""}
      onClick={onFocusRow}
      sx={{
        position: "relative", display: "grid", gridTemplateColumns: { xs: "24px minmax(0,1fr)", sm: "28px minmax(0,1fr) auto" },
        gap: { xs: 1.25, sm: 1.75 }, px: 2.5, py: 2.25, borderBottom: `1px solid ${T.border}`,
        bgcolor: current && !score ? "#fbfaff" : "transparent", "&:last-of-type": { borderBottom: 0 },
        "&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 3, bgcolor: barColor },
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: "var(--font-mono)", color: T.ink, opacity: 0.5, pt: "2px" }}>{idx + 1}</Typography>
      <Box sx={{ minWidth: 0 }}>
        {item.criteria && (
          <Typography sx={{ fontSize: 15, lineHeight: 1.55, color: T.ink, opacity: score === "na" ? 0.55 : 1, textWrap: "pretty" }}>{item.criteria}</Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1 }}>
          {item.isCritical && (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.missed, bgcolor: "var(--color-danger-bg)", px: "7px", py: "3px", borderRadius: "4px" }}>
              <WarningAmberIcon sx={{ fontSize: 11 }} />Critical
            </Box>
          )}
          {isObservation && (
            <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--color-status-neutral)", bgcolor: "rgba(121,98,140,.14)", px: "7px", py: "3px", borderRadius: "4px" }}>Observe</Box>
          )}
          <Typography component="span" sx={{ fontSize: 12, opacity: 0.55 }}>{itemPoints(item)} pt</Typography>
          {!locked && (
            <Box
              component="button"
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowNote((v) => !v); }}
              sx={{ display: "inline-flex", alignItems: "center", gap: "5px", border: 0, bgcolor: "transparent", fontFamily: "inherit", fontSize: 12.5, cursor: "pointer", p: "2px 0", color: note ? "var(--color-accent-violet)" : T.ink, opacity: note ? 1 : 0.6, "&:hover": { opacity: 1 } }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 13 }} />{note ? "Note added" : "Add note"}
            </Box>
          )}
        </Box>
        <Collapse in={(showNote || noteRequired) && !locked}>
          <Box onClick={(e) => e.stopPropagation()} sx={{ display: "grid", gap: 0.75, mt: 1.25 }}>
            {noteRequired && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, fontWeight: 600, color: T.missed }}>
                Note required for missed items
                <Box component="span" sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>Required</Box>
              </Box>
            )}
            <TextField
              fullWidth multiline minRows={2} size="small"
              placeholder={score === "missed" ? "Describe what was missed (required)…" : "Add a note for this step…"}
              value={note || ""} onChange={(e) => onNote(e.target.value)} variant="outlined" error={noteRequired}
              sx={{
                "& .MuiOutlinedInput-root": { fontSize: 14, borderRadius: "10px", bgcolor: "#fff" },
                "& .MuiOutlinedInput-notchedOutline": noteRequired
                  ? { borderColor: `${T.missed} !important` }
                  : { borderColor: `${T.border} !important` },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--color-accent-violet) !important" },
              }}
            />
          </Box>
        </Collapse>
      </Box>
      <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
        <ScoreToggle value={score} onChange={onScore} disabled={locked} isObservation={isObservation} fullWidthOnMobile />
      </Box>
    </Box>
  );
}

// ─── Section Group ────────────────────────────────────────────────────────────
export function SectionGroup({ section, sectionIdx, scores, notes, onScore, onNote, locked, currentId, onFocusItem }) {
  const sectionEarned = section.items.reduce((sum, item) => sum + (scores[item.id] === "performed" ? itemPoints(item) : 0), 0);
  const sectionTotal = section.items.reduce((sum, item) => sum + itemPoints(item), 0);

  return (
    <Paper elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, overflow: "hidden", mb: 2 }}>
      <Box sx={{ px: 2.5, py: 2.25, display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${T.border}` }}>
        <Box sx={{ width: 26, height: 26, borderRadius: "6px", bgcolor: T.primary, color: "#fff", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700, fontFamily: "var(--font-mono)", flexShrink: 0 }}>
          {String(sectionIdx + 1).padStart(2, "0")}
        </Box>
        <Typography component="h2" sx={{ fontSize: 17, fontWeight: 600, color: T.ink, fontFamily: "var(--font-display)", mr: "auto" }}>{section.title}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: "var(--font-mono)", opacity: 0.7, whiteSpace: "nowrap" }}>{sectionEarned} / {sectionTotal} pts</Typography>
      </Box>
      <Box>
        {section.items.map((item, idx) => (
          <CriteriaRow key={item.id ?? `${sectionIdx}-${idx}`} idx={idx} item={item} score={scores[item.id] ?? null} note={notes[item.id] ?? ""}
            onScore={(val) => onScore(item.id, val)} onNote={(val) => onNote(item.id, val)} locked={locked}
            current={currentId != null && currentId === item.id} onFocusRow={onFocusItem ? () => onFocusItem(item.id) : undefined} />
        ))}
      </Box>
    </Paper>
  );
}

// Group a flat items array into sections by item.sectionTitle, same as the
// instructor assessment page — fallback to one implicit group when unset.
export function groupItemsIntoSections(items) {
  if (!items || !items.length) return [];
  const grouped = {};
  const order = [];
  items.forEach((item) => {
    const key = item.sectionTitle || "__default__";
    if (!grouped[key]) {
      grouped[key] = { title: item.sectionTitle || "Performance Criteria", items: [] };
      order.push(key);
    }
    grouped[key].items.push(item);
  });
  return order.map((k) => grouped[k]);
}
